import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'
import { DirectReportsTable } from '@/features/direct-reports'
import { useMyDirectReports } from '@/features/direct-reports/services/direct-reports.convex'
import { useConvexCreateAssessment } from '@/features/assessments/services/assessments.convex'
import { useMyConvexUserId, useMyConvexOrganizationId } from '@/features/auth'
import { PeriodConfigRequiredBanner } from '@/features/organization'
import { usePeriodReadiness } from '@/features/organization/services/period-config.convex'

export const Route = createFileRoute('/(dashboard)/direct-reports/')({
  component: RouteComponent,
})

function RouteComponent() {
  const navigate = useNavigate()
  const convexUserId = useMyConvexUserId()
  const convexOrgId = useMyConvexOrganizationId()
  const [isCreatingAssessment, setIsCreatingAssessment] = useState(false)

  const { results, status, loadMore } = useMyDirectReports()
  const createAssessmentMutation = useConvexCreateAssessment()
  const readiness = usePeriodReadiness(convexOrgId)

  const periodReady = readiness.status === 'ready'
  const periodMessage =
    readiness.status === 'not-configured'
      ? 'Configure assessment periods first in organization settings.'
      : readiness.status === 'awaiting-start'
        ? `Assessments unlock on ${new Date(readiness.startDate).toLocaleDateString()}.`
        : readiness.status === 'no-current-period'
          ? 'No active assessment period — open organization settings to generate one.'
          : null

  const handleCreateAssessment = (employeeUserId: string) => {
    if (!convexOrgId) return
    if (!periodReady) {
      if (periodMessage) toast.error(periodMessage)
      return
    }
    setIsCreatingAssessment(true)
    createAssessmentMutation
      .mutateAsync({
        employeeUserId,
        organizationId: convexOrgId,
      })
      .then(() => {
        toast.success('Assessment created successfully!')
      })
      .catch((err: unknown) => {
        const msg =
          err instanceof Error ? err.message : 'Failed to create assessment'
        toast.error(msg)
      })
      .finally(() => {
        setIsCreatingAssessment(false)
      })
  }

  const handleTakeSurvey = (assessmentId: string, responseId: string) => {
    void navigate({
      to: '/assessments/$assessmentId/responses/$id',
      params: { assessmentId, id: responseId },
    })
  }

  return (
    <div className="space-y-6">
      <PeriodConfigRequiredBanner />
      <DirectReportsTable
        directReports={results}
        currentUserId={convexUserId ?? ''}
        onCreateAssessment={handleCreateAssessment}
        onTakeSurvey={handleTakeSurvey}
        // Reuse the "creating" flag to also disable the per-row buttons while
        // the org has no active period — the banner above explains why.
        isCreatingAssessment={isCreatingAssessment || !periodReady}
        isLoading={status === 'LoadingFirstPage'}
        hasMore={status === 'CanLoadMore'}
        isLoadingMore={status === 'LoadingMore'}
        onLoadMore={() => loadMore(20)}
      />
    </div>
  )
}
